<template>
  <div class="rounded-lg bg-gray-400 text-gray-700 overflow-hidden">
    <div class="px-4 sm:px-6 pt-5 pb-2">
      <div class="text-xl sm:text-2xl font-bold leading-tight">
        {{ editItem ? 'Edit item' : 'Did you receive any items not on the invoice?' }}
      </div>
    </div>

    <div class="px-4 sm:px-6 pb-5 space-y-4 mt-4">
      <!-- Product selection -->
      <div>
        <label class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
          Product <span class="text-red-500">*</span>
        </label>

        <div v-if="productChosen" class="flex items-center gap-2 py-3 px-4 rounded-lg bg-gray-300">
          <span class="flex-1 text-sm font-medium text-gray-700">
            {{ selectedProduct ? selectedProduct.name : 'Generic Product Type' }}
          </span>
          <button @click="clearProduct" class="text-xs text-gray-500 hover:text-gray-700 font-medium">
            Change
          </button>
        </div>

        <div v-else>
          <input
            v-model="productSearch"
            type="text"
            placeholder="Search products…"
            class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:bg-white focus:border-gray-500 mb-2"
          />
          <div class="rounded-lg bg-gray-300 overflow-y-auto" style="max-height: 9rem">
            <div
              @click="selectNewProduct"
              class="flex items-center px-3 py-3 border-b border-gray-400 cursor-pointer hover:bg-gray-200 text-sm font-medium text-gray-500 italic"
            >
              Generic Product Type
            </div>
            <div
              v-for="prod in filteredProducts"
              :key="prod.id"
              @click="selectProduct(prod)"
              class="flex items-center px-3 py-3 border-b border-gray-400 last:border-0 cursor-pointer hover:bg-gray-200 text-sm text-gray-700"
            >
              {{ prod.name }}
            </div>
            <div v-if="filteredProducts.length === 0 && productSearch" class="px-3 py-4 text-center text-xs text-gray-500">
              No products found
            </div>
          </div>
        </div>
      </div>

      <template v-if="productChosen">
        <!-- Description — generic items only -->
        <div v-if="!selectedProductId">
          <label class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
            Category <span class="text-red-500">*</span>
          </label>
          <select
            v-model="description"
            class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
          >
            <option value="" disabled>Select a category…</option>
            <option>Fresh Produce</option>
            <option>Dry Goods</option>
            <option>Meat / Seafood</option>
            <option>Dairy / Refrigerated</option>
            <option>Beverages</option>
            <option>Condiments &amp; Sauces</option>
            <option>Packaging / Supplies</option>
            <option>Other</option>
          </select>
        </div>

        <!-- Quantity -->
        <div>
          <label class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
            Quantity Received <span class="text-red-500">*</span>
          </label>
          <input
            v-model.number="quantity"
            type="number"
            min="1"
            class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 text-2xl font-bold text-center"
            placeholder="1"
          />
        </div>

        <!-- Photo of product -->
        <div>
          <label class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
            Photo of Product
            <span v-if="disposition === 'accepted' || !editItem" class="normal-case font-normal text-red-500">(required to accept)</span>
            <span v-else class="normal-case font-normal text-gray-500">(optional)</span>
          </label>
          <div v-if="photoUrl" class="mb-3">
            <img :src="photoUrl" class="w-full max-h-40 object-cover rounded-lg" />
            <button @click="photoUrl = null" class="mt-1 text-xs text-red-600 hover:text-red-800">Remove</button>
          </div>
          <camera-capture v-else :is-developer-or-admin="isDeveloperOrAdmin" @captured="photoUrl = $event" />
        </div>

        <!-- Close-up of label -->
        <div>
          <label class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
            Close-up of Label
            <span v-if="disposition === 'accepted' || !editItem" class="normal-case font-normal text-red-500">(required to accept)</span>
            <span v-else class="normal-case font-normal text-gray-500">(optional)</span>
          </label>
          <div v-if="labelPhotoUrl" class="mb-3">
            <img :src="labelPhotoUrl" class="w-full max-h-40 object-cover rounded-lg" />
            <button @click="labelPhotoUrl = null" class="mt-1 text-xs text-red-600 hover:text-red-800">Remove</button>
          </div>
          <camera-capture v-else :is-developer-or-admin="isDeveloperOrAdmin" @captured="labelPhotoUrl = $event" />
        </div>

        <!-- Disposition -->
        <div>
          <label class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
            What are you doing with this item? <span class="text-red-500">*</span>
          </label>
          <div class="flex gap-2">
            <!-- Return to Vendor -->
            <button
              @click="selectDisposition('returned')"
              :disabled="!editItem && !canReturn"
              class="flex-1 py-3 px-3 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40"
              :class="disposition === 'returned'
                ? 'bg-red-700 text-white ring-2 ring-red-400'
                : 'bg-gray-300 text-gray-600 hover:bg-gray-200'"
            >
              Return to Vendor
            </button>
            <!-- Accept Delivery -->
            <button
              @click="selectDisposition('accepted')"
              :disabled="!editItem && !canAccept"
              class="flex-1 py-3 px-3 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40"
              :class="disposition === 'accepted'
                ? 'bg-gray-700 text-white ring-2 ring-gray-500'
                : 'bg-gray-300 text-gray-600 hover:bg-gray-200'"
            >
              Accept Delivery
            </button>
          </div>
        </div>

        <!-- Save / Cancel — edit mode only -->
        <div v-if="editItem" class="flex gap-2 pt-2">
          <button
            @click="$emit('cancel')"
            class="py-3 px-5 rounded-lg bg-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="submit"
            :disabled="!canSave"
            class="flex-1 py-3 px-5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40"
            :class="canSave ? 'bg-gray-700 text-white hover:bg-gray-800' : 'bg-gray-300 text-gray-500'"
          >
            Save
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import CameraCapture from '@/components/CameraCapture'
import { newId } from '@/utils'

export default {
  name: 'ReceivingMispickForm',
  components: { CameraCapture },
  props: {
    isDeveloperOrAdmin: { type: Boolean, default: false },
    products: { type: Array, default: () => [] },
    editItem: { type: Object, default: null }
  },
  data() {
    return {
      productSearch: '',
      selectedProductId: null,
      productChosen: false,
      description: '',
      quantity: null,
      photoUrl: null,
      labelPhotoUrl: null,
      disposition: null
    }
  },
  computed: {
    filteredProducts() {
      if (!this.productSearch) return this.products
      const q = this.productSearch.toLowerCase()
      return this.products.filter((p) => p.name.toLowerCase().includes(q))
    },
    selectedProduct() {
      if (!this.selectedProductId) return null
      return this.products.find((p) => p.id === this.selectedProductId) || null
    },
    baseValid() {
      if (!this.productChosen) return false
      if (!this.selectedProductId && !this.description) return false
      return !!this.quantity
    },
    canReturn() {
      return this.baseValid
    },
    canAccept() {
      return this.baseValid && !!(this.photoUrl && this.labelPhotoUrl)
    },
    canSave() {
      if (!this.disposition) return false
      return this.disposition === 'returned' ? this.canReturn : this.canAccept
    }
  },
  watch: {
    editItem: {
      immediate: true,
      handler(val) {
        if (val) this.prefillWith(val)
      }
    }
  },
  methods: {
    selectProduct(prod) {
      this.selectedProductId = prod.id
      this.productChosen = true
      this.productSearch = ''
    },
    selectNewProduct() {
      this.selectedProductId = null
      this.productChosen = true
      this.productSearch = ''
    },
    clearProduct() {
      this.selectedProductId = null
      this.productChosen = false
      this.productSearch = ''
    },
    selectDisposition(d) {
      this.disposition = d
      if (!this.editItem) {
        // Add mode: submit immediately if requirements are met
        this.submit()
      }
    },
    prefillWith(item) {
      this.selectedProductId = item.productId || null
      this.productChosen = true
      this.productSearch = ''
      this.description = item.description || ''
      this.quantity = item.quantity || null
      this.photoUrl = item.photoUrl || null
      this.labelPhotoUrl = item.labelPhotoUrl || null
      this.disposition = item.disposition || null
    },
    submit() {
      if (!this.canSave) return
      const desc = this.selectedProduct ? this.selectedProduct.name : this.description
      this.$emit('add', {
        id: this.selectedProductId || newId(),
        productId: this.selectedProductId || null,
        description: desc,
        quantity: this.quantity,
        photoUrl: this.photoUrl,
        labelPhotoUrl: this.labelPhotoUrl,
        disposition: this.disposition
      })
      if (!this.editItem) {
        this.productSearch = ''
        this.selectedProductId = null
        this.productChosen = false
        this.description = ''
        this.quantity = null
        this.photoUrl = null
        this.labelPhotoUrl = null
        this.disposition = null
      }
    }
  }
}
</script>
